import { RubyGemMark } from '../../../components/RubyGemMark';
/**
 * THE fleet table. There is exactly one in this product and it lives here, on
 * the Fleet tab of `/pos-config` — the device is managed in one place, next to
 * the branding and install flows that already configure it. `/releases` shows
 * rollout PROGRESS and links in here; it must never render a device list.
 *
 * Every column this table rendered before release data existed still renders,
 * with the same value (Channel, Boot, App load, Install, Last check-in). The
 * release columns are additive and read "—" for a device with no release data.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, History, LifeBuoy, RefreshCw, Monitor, RotateCcw, Trash2,
  LayoutGrid, List as ListIcon, Search, X, Copy,
} from 'lucide-react';
import { deviceConfigApi } from '../../../services/deviceConfigApi';
import { toastService } from '../../../services/toastService';
import { type DeviceModelRow } from '../../../services/hardwareApi';
import ShimmerImage from '../../../components/ShimmerImage';
import {
  buildFleetRows,
  classifyHardwareMatch,
  isHardwareMismatch,
  tallyFleetRowStatuses,
  type FleetDeviceRecord,
  type FleetReleaseStatus,
  type FleetRow,
  type FleetUnitRecord,
  type FleetRegisterLinkMap,
  type ResolvedTargetMap,
} from '../fleet/fleetRows';
import type { FleetActionId, DeviceHealthDeviceStat } from '@platform/shared-types';
import { fmtAgo } from '../../../components/deviceHealth/healthPanelModel';
import DeviceHistoryModal from '../fleet/DeviceHistoryModal';
import FleetDeviceDrawer from '../fleet/FleetDeviceDrawer';
import FleetDeviceCard from '../fleet/FleetDeviceCard';
import { useDeviceHref } from '../device/deviceLinkBase';
import FleetRowMenu from '../fleet/FleetRowMenu';
import ReinstallModal from '../../inventory/ReinstallModal';
import {
  STATUS_STYLE,
  TARGET_SOURCE_STYLE,
  RING_STYLE,
  SOURCE_STYLE,
  shortVersion,
  installedOn,
  installedAtFull,
  installCell,
  reinstallBlock,
  checkInAgo,
} from '../fleet/fleetPresentation';

/** A device seen this recently is treated as live for the removal warning. */
const LIVE_WITHIN_MS = 10 * 60 * 1000;

/** An elision in the pager, distinct from any page number. */
const GAP = -1;

/** Errors24h above this is emphasised (bold red) in the fleet table — a glance threshold, not an alert/SLA line. */
const ERRORS_24H_HOT_THRESHOLD = 20;

/**
 * The page numbers to draw: first, last, the current page ±2, and a gap marker
 * where numbers were left out. One button per page drew 167 buttons on a
 * 2000-device fleet.
 */
export function pageWindow(page: number, totalPages: number): number[] {
  const wanted = new Set<number>([1, totalPages]);
  for (let p = page - 2; p <= page + 2; p += 1) {
    if (p >= 1 && p <= totalPages) wanted.add(p);
  }
  const out: number[] = [];
  let prev = 0;
  for (const p of [...wanted].sort((a, b) => a - b)) {
    // A gap standing for ONE page is worse than the page itself — draw it.
    if (prev && p - prev === 2) out.push(prev + 1);
    else if (prev && p - prev > 2) out.push(GAP);
    out.push(p);
    prev = p;
  }
  return out;
}

/**
 * REMOVE FROM FLEET — confirmation.
 *
 * A custom dialog, not `window.confirm`: native dialogs are banned platform-wide
 * and are invisible inside the iOS webview.
 *
 * The dialog's job is to tell the truth about what removal does. Check-in is an
 * unauthenticated UPSERT, so a terminal still running the app re-registers
 * within a minute — for a device that checked in moments ago, removal is
 * genuinely temporary, and saying so up front is the difference between a tool
 * and a trap.
 */
const RemoveDeviceModal: React.FC<{
  row: FleetRow;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}> = ({ row, busy, onClose, onConfirm }) => {
  const seenRecently = !!row.updatedAt && Date.now() - row.updatedAt < LIVE_WITHIN_MS;
  const label = row.registerName || row.deviceId || 'this device';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="w-full max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-50">
            <Trash2 size={17} className="text-red-600" />
          </div>
          <div className="min-w-0">
            <h3 className="text-[15px] font-extrabold text-gray-900">Remove from fleet?</h3>
            <p className="mt-1 text-[12.5px] text-gray-600">
              <b className="text-gray-900">{label}</b>
              {row.locationName ? ` at ${row.locationName}` : ''} will be removed from the fleet list.
            </p>
          </div>
        </div>

        {seenRecently ? (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-[11.5px] text-amber-800">
            <AlertTriangle size={14} className="mt-0.5 shrink-0" />
            <div>
              <b>This device checked in minutes ago.</b> Removing the record does not stop it —
              it will re-register on its next check-in and reappear here. Use this for a device
              that is genuinely gone.
            </div>
          </div>
        ) : (
          <p className="mt-3 rounded-lg bg-gray-50 p-2.5 text-[11.5px] text-gray-600">
            If this device is ever powered on with the POS app installed, it will re-register
            and reappear.
          </p>
        )}

        {row.pinnedVersion && (
          <p className="mt-2 rounded-lg border border-rose-200 bg-rose-50 p-2.5 text-[11.5px] text-rose-700">
            Its pin to <b className="font-mono">{row.pinnedVersion}</b> is discarded. If it comes
            back it follows the <b>{row.releaseRing}</b> ring instead.
          </p>
        )}

        <p className="mt-2 text-[11px] text-gray-400">
          The version history for this device is kept — an audit record outlives the row.
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="min-h-[44px] rounded-xl px-4 text-[12.5px] font-bold text-gray-600 hover:bg-gray-100 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="inline-flex min-h-[44px] items-center gap-2 rounded-xl bg-red-600 px-4 text-[12.5px] font-extrabold text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy && <RubyGemMark size={14} drawing color="currentColor" />}
            {busy ? 'Removing…' : 'Remove from fleet'}
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Copy a provisioning code to the clipboard.
 *
 * NEVER `console.log` the code — it is the credential a till claims itself
 * with. Mirrors the identical helper in `FleetRowMenu`; both views offer the
 * copy because this is one of the few strings an operator searches a fleet by.
 */
const copyProvisionCode = async (code: string) => {
  try {
    await navigator.clipboard.writeText(code);
    toastService.success('Provisioning code copied');
  } catch {
    toastService.error('Could not copy — copy it by hand');
  }
};

export default function FleetTable({
  deviceType,
  devices,
  onChanged,
  units = [],
  models = [],
  resolvedTargets = {},
  targetsUnavailable = false,
  targetsTruncated = false,
  devicesError = null,
  devicesTruncated = false,
  onRetry,
  registerLinks = {},
  registerMissing = [],
  registerUnresolved = [],
  tenantNames = {},
  orders = {},
  enrichmentError = null,
  health,
}: {
  deviceType: string;
  devices: FleetDeviceRecord[];
  onChanged: () => void;
  /**
   * Hardware units we have installed an OS on. They supply the "last known
   * from install" version, the install stamp, the device photo and the order
   * trace — and any unit with no check-in of its own becomes its own row, so
   * the fleet is every device we shipped rather than only the online ones.
   *
   * A unit joins its check-in on the paired terminal id, or failing that on
   * its tracking id inside the register name — see `buildFleetRows`. It is
   * NOT joined on `unit.terminalId`, which is a payment-processor TID.
   */
  units?: FleetUnitRecord[];
  /** Hardware catalog, for the device photo. Rows render fine without it. */
  models?: DeviceModelRow[];
  /**
   * deviceId → the target the server resolved for that device. Optional: with
   * no source wired the Target/Drift columns read "unknown" rather than
   * guessing — a target must never be inferred client-side from a pin alone,
   * because the server falls back to the ring when a pinned build is missing.
   */
  resolvedTargets?: ResolvedTargetMap;
  /**
   * True when the resolved-target read itself failed or is not wired. The
   * table then SAYS the target column is unavailable rather than rendering
   * every device as a silent "—", which is indistinguishable from "the server
   * resolved nothing" and would be read as "nothing to do".
   */
  targetsUnavailable?: boolean;
  /**
   * True when the resolved-target read SUCCEEDED but the server said it
   * stopped short of the whole fleet. Devices past the cut have no target and
   * would otherwise render an ordinary "—" / "unknown", indistinguishable from
   * "the server resolved nothing" — the truncation must be said out loud, never
   * inferred from a suspiciously round count.
   */
  targetsTruncated?: boolean;
  /**
   * Message when the check-in read itself FAILED. `null` means it succeeded
   * (and an empty `devices` then genuinely means no device has checked in).
   * The table must never assert "no devices have checked in yet" from a read
   * that never returned.
   */
  devicesError?: string | null;
  /** True when the check-in read succeeded but returned a partial fleet. */
  devicesTruncated?: boolean;
  /** Re-run the reads behind this table. */
  onRetry?: () => void;
  /**
   * registerId → its register (the fleet's deviceId IS the registerId). Best
   * effort — empty when the lookup failed, never a blocker for the rest of
   * the table.
   */
  registerLinks?: FleetRegisterLinkMap;
  /**
   * Device ids the register lookup DEFINITELY found no register for — a real
   * "not paired" answer from the server. Not yet rendered here (Task 7/8);
   * kept apart from `registerUnresolved` because merging the two would
   * render "we don't know" as "there is no register".
   */
  registerMissing?: string[];
  /** Device ids the register lookup could not resolve because the read itself failed. "We don't know" — never "no register". */
  registerUnresolved?: string[];
  /** tenantId → display name, for rows joined to a register. */
  tenantNames?: Record<string, string>;
  /** orderId → { orderNo, status }, for rows joined to a register/unit order. */
  orders?: Record<string, { orderNo: string; status: string }>;
  /** Which enrichment (register links / tenant names / order status) degraded this cycle, or null. Not yet rendered here (Task 7/8). */
  enrichmentError?: string | null;
  /**
   * deviceId → its last-24h device-health stats (Task 23's `DeviceHealthPanel`
   * `onSummary`). Optional — the legacy POS Config view never supplies it, and
   * both health columns then read "—", never 0: unknown is not zero.
   */
  health?: Record<string, DeviceHealthDeviceStat>;
}) {
  const deviceHref = useDeviceHref();
  const [searchParams, setSearchParams] = useSearchParams();
  const [history, setHistory] = useState<{ deviceId: string; label: string } | null>(null);
  /** The row awaiting a remove confirmation, or null. */
  const [removing, setRemoving] = useState<FleetRow | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  /** The row awaiting a reinstall confirmation, or null. */
  const [reinstalling, setReinstalling] = useState<FleetRow | null>(null);
  /** The row whose Fleet Device Support drawer is open, or null. */
  const [support, setSupport] = useState<FleetRow | null>(null);
  /**
   * When the drawer was opened via the `⋯` menu's "Re-provision", the action
   * it should jump straight into — `clear_pairing`'s existing tier-3 flow.
   * Cleared whenever the drawer opens any other way.
   */
  const [supportInitialAction, setSupportInitialAction] = useState<FleetActionId | null>(null);
  /** `?unitId=` already consumed, so a later row-list refresh does not re-open a drawer the operator closed. */
  const [unitIdConsumed, setUnitIdConsumed] = useState(false);

  const openReprovision = (d: FleetRow) => {
    setSupportInitialAction('clear_pairing');
    setSupport(d);
  };

  const modelById = useMemo(
    () => new Map(models.map((m) => [m.modelId, m])),
    [models],
  );

  const rows = useMemo(
    () => buildFleetRows(devices, units, resolvedTargets, { deviceType }, registerLinks),
    [devices, units, resolvedTargets, deviceType, registerLinks],
  );
  // Tally what `buildFleetRows` already decided — do NOT classify a second
  // time. Two classifications mean two `Date.now()` values, and a device on the
  // staleness boundary would be counted in one bucket and filtered into another.
  const counts = useMemo(() => tallyFleetRowStatuses(rows), [rows]);

  // `?unitId=` deep-links straight into a device's support drawer
  // (`/pos-config?tab=fleet&unitId=…`), consumed by `DeviceInstallPanel.tsx`
  // elsewhere on this same query param. Open it once the matching row shows
  // up; `unitIdConsumed` stops a later `rows` refresh (e.g. after issuing a
  // command elsewhere) from re-opening a drawer the operator already closed.
  useEffect(() => {
    if (unitIdConsumed) return;
    const wanted = searchParams.get('unitId');
    if (!wanted) return;
    const match = rows.find((r) => r.unitId === wanted);
    if (match) {
      setSupport(match);
      setUnitIdConsumed(true);
    }
  }, [rows, searchParams, unitIdConsumed]);

  const closeSupport = () => {
    setSupport(null);
    setSupportInitialAction(null);
    setUnitIdConsumed(true);
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete('unitId');
      return params;
    }, { replace: true });
  };

  // `?status=` lets the Rollout tab link in here pre-filtered. An unrecognised
  // value shows everything rather than an empty table.
  const statusFilter = searchParams.get('status') as FleetReleaseStatus | null;
  const active = statusFilter && STATUS_STYLE[statusFilter] ? statusFilter : null;
  const statusFiltered = active ? rows.filter((r) => r.releaseStatus === active) : rows;

  // A filter that leaves the operator on a stale page shows an empty view
  // that reads as "nothing matched" — every setter below that can change
  // WHICH rows match also clears `page` in the same URL update.
  const setFilter = (next: FleetReleaseStatus | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next) params.set('status', next); else params.delete('status');
      params.delete('page');
      return params;
    }, { replace: true });
  };

  // `?view=card|list`. LIST is the default — it is the view an operator scans a
  // fleet in; the card is for reading one device closely. The default is never
  // written to the URL, matching how `status=`all and `page=`1 are also left out.
  const view: 'card' | 'list' = searchParams.get('view') === 'card' ? 'card' : 'list';
  const setView = (next: 'card' | 'list') => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next === 'card') params.set('view', 'card'); else params.delete('view');
      return params;
    }, { replace: true });
  };

  // `?q=` — one box, matched case-insensitively against business name,
  // provisioning code, order number, register name, tracking id and serial.
  const q = searchParams.get('q') || '';
  const setQuery = (next: string) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next) params.set('q', next); else params.delete('q');
      params.delete('page');
      return params;
    }, { replace: true });
  };
  const qLower = q.trim().toLowerCase();

  /**
   * Device ids whose register lookup FAILED. "We don't know", never "no
   * register" — only THREE of the six searchable fields come from that
   * lookup (business name, provisioning code, order number); `registerName`
   * comes from the device's own check-in and `trackingId`/`serialNumber` come
   * from the hardware unit, so all three survive a failed lookup and MUST
   * still be tested. Excluding the whole row would make an operator who
   * reads a serial straight off an unresolved card (the mockup's card 5
   * prints one) get told no device matches it — the exact lie the honesty
   * panel below exists to prevent, reintroduced one layer up.
   */
  const unresolvedIds = useMemo(() => new Set(registerUnresolved), [registerUnresolved]);
  const isUnresolved = (r: FleetRow) => !!r.deviceId && unresolvedIds.has(r.deviceId);
  const bizName = (r: FleetRow) => (r.tenantId ? tenantNames[r.tenantId] : undefined);

  const matchesQuery = (r: FleetRow): boolean => {
    if (!qLower) return true;
    const fields: (string | undefined)[] = [r.registerName ?? undefined, r.trackingId, r.serialNumber];
    if (!isUnresolved(r)) {
      const orderNo = r.orderId ? orders[r.orderId]?.orderNo : undefined;
      fields.push(bizName(r), r.provisionCode, orderNo);
    }
    return fields.some((f) => !!f && f.toLowerCase().includes(qLower));
  };
  const queried = qLower ? statusFiltered.filter(matchesQuery) : statusFiltered;

  // Default sort: business name, then register name. A row with no resolved
  // business sorts LAST, under its own section label — never interleaved
  // silently among named businesses.
  const sorted = useMemo(() => [...queried].sort((a, b) => {
    const ba = bizName(a); const bb = bizName(b);
    const ga = ba ? 0 : 1; const gb = bb ? 0 : 1;
    if (ga !== gb) return ga - gb;
    const bizCmp = (ba || '').localeCompare(bb || '');
    if (bizCmp !== 0) return bizCmp;
    return (a.registerName || '').localeCompare(b.registerName || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [queried, tenantNames]);

  // ONE page size for both views — the brief is explicit that switching the
  // view must never change WHICH devices are shown, only how they are drawn.
  // A per-view size (12 cards / 8 rows) meant page 1 of one view and page 1 of
  // the other were different slices of the same sorted list.
  const PAGE_SIZE = 12;
  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageParam = parseInt(searchParams.get('page') || '1', 10);
  const page = Math.min(Math.max(1, Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1), totalPages);
  const pageStart = (page - 1) * PAGE_SIZE;
  const shown = sorted.slice(pageStart, pageStart + PAGE_SIZE);
  // The "No business resolved" label is computed PER PAGE (not once over the
  // whole sorted list) — otherwise a group that straddles a page boundary
  // shows unlabelled no-business rows on every page after the first.
  const noBusinessLabelKey = shown.find((r) => !bizName(r))?.key ?? null;
  const setPage = (next: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next <= 1) params.delete('page'); else params.set('page', String(next));
      return params;
    }, { replace: true });
  };

  /** Rows within the CURRENT status filter whose register lookup failed — the basis for the search's honesty panel. */
  const unresolvedInScope = statusFiltered.filter(isUnresolved);

  function emptyState(): React.ReactNode {
    if (rows.length === 0) {
      // Never claim the fleet is empty on the strength of a read that failed.
      return devicesError
        ? 'Device check-ins could not be loaded — the fleet is unknown, not empty.'
        : 'No devices have checked in yet.';
    }
    if (qLower) {
      // Every row IS searched now (register name / tracking id / serial always
      // are) — an unresolved row is searched on fewer FIELDS, not skipped, so
      // there is no "N of M" split to report any more.
      return (
        <div className="mx-auto max-w-xl text-left">
          <div className="text-center text-[14px] font-extrabold text-gray-900">No device matches &ldquo;{q}&rdquo;</div>
          <div className="mt-1 text-center text-[12.5px] text-gray-500">
            Searched the business name, provisioning code, order number, register name, tracking id and serial of{' '}
            <b className="text-gray-700">{statusFiltered.length} device{statusFiltered.length === 1 ? '' : 's'}</b>.
          </div>
          {unresolvedInScope.length > 0 && (
            <div role="status" className="mt-3.5 rounded-xl border border-amber-300 bg-amber-50 p-3">
              <div className="flex items-center gap-1.5 text-[12.5px] font-extrabold text-amber-800">
                <AlertTriangle size={14} className="shrink-0" />
                {unresolvedInScope.length} device{unresolvedInScope.length === 1 ? ' was' : 's were'} searched on fewer fields
              </div>
              <p className="mt-1.5 text-[12px] text-amber-900">
                Their register lookup failed, so their business name, provisioning code and order number could
                not be read — a match on those three fields could have been missed.{' '}
                <b>This code may still belong to one of them.</b> It is not that the code does not exist there;
                it is that we could not look at those fields.
              </p>
              <ul className="mt-1.5 list-disc pl-4 text-[12px] text-amber-900">
                {unresolvedInScope.slice(0, 5).map((r) => (
                  <li key={r.key}>
                    <b className="font-mono">{r.deviceId}</b> — {bizName(r) || 'business unavailable'}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      );
    }
    return `No devices are ${active ? STATUS_STYLE[active].label : 'matching'}.`;
  }

  const flip = async (d: FleetRow) => {
    if (!d.deviceId) return;
    const next = d.assignedChannel === 'canary' ? 'stable' : 'canary';
    try {
      await deviceConfigApi.setDeviceChannel(deviceType, d.deviceId, next);
      toastService.success(`${d.registerName || d.deviceId} → ${next}`);
      onChanged();
    } catch (e: any) { toastService.error(e?.message || 'Channel change failed'); }
  };

  return (
    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm p-4">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {/*
          ONE basis for both numbers: `counts.total` is a tally of the rows this
          table renders, and the "All" pill below tallies the same rows. Using
          `devices.length` here would under-count the moment an installed-but-
          never-checked-in unit produces a row with no device record.
        */}
        <div className="text-sm font-extrabold text-gray-900">Fleet · <span className="text-emerald-600">{counts.total} devices</span></div>
        <div className="text-[11.5px] text-gray-400">rows below · self-registered from check-ins plus any installed unit that has never checked in</div>
      </div>

      {devicesError && (
        <div
          role="alert"
          className="flex items-start gap-2 mb-3 rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-[11.5px] font-bold text-red-700"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div className="space-y-1">
            <div>
              Could not load device check-ins — this table is NOT showing the fleet.
              An empty table below means the read failed, not that no device has checked in.
            </div>
            <div className="font-normal opacity-80">{devicesError}</div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-xl bg-stone-800 text-white text-[11.5px] font-extrabold hover:bg-stone-700"
              >
                <RefreshCw size={13} /> Retry
              </button>
            )}
          </div>
        </div>
      )}

      {(devicesTruncated || targetsTruncated) && (
        <div
          role="status"
          className="flex items-start gap-2 mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-[11.5px] font-bold text-amber-700"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            {devicesTruncated
              ? 'The server returned only part of this fleet — devices past the cut are missing from the table and from every count above.'
              : 'The server resolved targets for only part of this fleet — devices past the cut show no Target and are counted as Unknown, not as on target.'}
            {' '}This is a partial view; it is not evidence that anything is wrong with a device.
          </span>
        </div>
      )}

      {/*
        F4 — an enrichment that degraded must SAY SO. `enrichmentError` was
        threaded in and read by nothing, so a failed tenant or order list
        showed blank business names and no order pills with no explanation.
        Same voice as `targetsUnavailable` below: what is unavailable, and an
        explicit statement that it says nothing about any device.
      */}
      {enrichmentError && (
        <div
          role="status"
          className="flex items-start gap-2 mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-[11.5px] font-bold text-amber-700"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <div className="space-y-1">
            <div>
              {enrichmentError}{' '}
              <span className="font-normal">
                Every other column below is live. This is a read that did not return — it is not
                evidence that anything is wrong with any device.
              </span>
            </div>
            {onRetry && (
              <button
                onClick={onRetry}
                className="inline-flex items-center gap-1.5 min-h-[44px] px-3 rounded-xl bg-stone-800 text-white text-[11.5px] font-extrabold hover:bg-stone-700"
              >
                <RefreshCw size={13} /> Retry the lookup and search again
              </button>
            )}
          </div>
        </div>
      )}

      {targetsUnavailable && (
        <div
          role="status"
          className="flex items-start gap-2 mb-3 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-[11.5px] font-bold text-amber-700"
        >
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Resolved targets unavailable — the Target and drift columns cannot be
            shown for any device right now. No device is being reported as on
            target on missing evidence; every other column below is live.
          </span>
        </div>
      )}

      {/* ── toolbar: view toggle · one search box ── */}
      <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-2">
        <div role="group" aria-label="View" className="flex shrink-0 gap-1 rounded-[11px] border border-gray-200 bg-gray-50 p-1">
          {/* List first: it is the default and the view a fleet is scanned in. */}
          <button
            type="button"
            onClick={() => setView('list')}
            aria-pressed={view === 'list'}
            className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-[12px] font-extrabold ${view === 'list' ? 'bg-[#DC143C] text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
          >
            <ListIcon size={14} /> List
          </button>
          <button
            type="button"
            onClick={() => setView('card')}
            aria-pressed={view === 'card'}
            className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-[12px] font-extrabold ${view === 'card' ? 'bg-[#DC143C] text-white shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
          >
            <LayoutGrid size={14} /> Cards
          </button>
        </div>
        <label className="flex h-11 min-w-[220px] flex-1 items-center gap-2 rounded-[11px] border border-gray-200 bg-white px-3 focus-within:border-[#DC143C] focus-within:ring-2 focus-within:ring-[#FFF1F3]">
          <Search size={15} className="shrink-0 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search business, provisioning code, order number, register…"
            aria-label="Search fleet"
            className="min-w-0 flex-1 border-0 bg-transparent text-[13px] text-gray-900 outline-none placeholder:text-gray-400"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear the search"
              title="Clear the search"
              className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X size={14} />
            </button>
          )}
        </label>
      </div>

      {/* Release status filter — the same buckets the Rollout tab counts. Composes with the search above. */}
      <div className="flex items-center gap-1.5 flex-wrap mb-3">
        <button
          onClick={() => setFilter(null)}
          className={`min-h-[44px] px-3 rounded-xl text-[11.5px] font-extrabold ${active === null ? 'bg-emerald-500/20 text-emerald-700' : 'text-gray-400 hover:bg-gray-100'}`}
        >
          All {counts.total}
        </button>
        {(Object.keys(STATUS_STYLE) as FleetReleaseStatus[]).map((s) => (
          <button
            key={s}
            onClick={() => setFilter(active === s ? null : s)}
            className={`min-h-[44px] px-3 rounded-xl text-[11.5px] font-extrabold ${active === s ? STATUS_STYLE[s].cls : 'text-gray-400 hover:bg-gray-100'}`}
          >
            {STATUS_STYLE[s].label} {counts[s]}
          </button>
        ))}
      </div>

      {/* ── results line + pager — the pill counts above stay whole-fleet; this line is the current page. ── */}
      <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1 px-0.5 text-[12px] text-gray-500">
        <span>
          Showing{' '}
          <b className="text-gray-900">{sorted.length === 0 ? 0 : pageStart + 1}–{Math.min(pageStart + PAGE_SIZE, sorted.length)}</b>
          {' '}of <b className="text-gray-900">{sorted.length}</b> device{sorted.length === 1 ? '' : 's'}
        </span>
        {totalPages > 1 && (
          <div className="ml-auto flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-gray-200 text-[12px] font-extrabold text-gray-500 disabled:opacity-40"
            >
              ‹
            </button>
            {pageWindow(page, totalPages).map((p, i) => (
              p === GAP ? (
                <span key={`gap${i}`} aria-hidden className="px-1 text-[12px] font-extrabold text-gray-300">…</span>
              ) : (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPage(p)}
                  aria-current={p === page ? 'page' : undefined}
                  className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-[12px] font-extrabold ${p === page ? 'bg-[#DC143C] text-white' : 'border border-gray-200 text-gray-500 hover:bg-gray-100'}`}
                >
                  {p}
                </button>
              )
            ))}
            <button
              type="button"
              onClick={() => setPage(page + 1)}
              disabled={page >= totalPages}
              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-gray-200 text-[12px] font-extrabold text-gray-500 disabled:opacity-40"
            >
              ›
            </button>
          </div>
        )}
      </div>

      {view === 'card' ? (
        shown.length === 0 ? (
          <div className="rounded-xl border border-dashed border-gray-200 px-4 py-10 text-center text-gray-400">
            {emptyState()}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 2xl:grid-cols-3">
            {shown.map((d) => (
              <React.Fragment key={d.key}>
                {d.key === noBusinessLabelKey && (
                  <div className="col-span-full mt-1 flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wide text-gray-400">
                    <span className="h-px flex-1 bg-gray-200" /> No business resolved <span className="h-px flex-1 bg-gray-200" />
                  </div>
                )}
                <FleetDeviceCard
                  row={d}
                  model={d.modelId ? modelById.get(d.modelId) : undefined}
                  tenantName={d.tenantId ? tenantNames[d.tenantId] : undefined}
                  order={d.orderId ? orders[d.orderId] : undefined}
                  registerUnresolved={isUnresolved(d)}
                  onSupport={() => setSupport(d)}
                  onHistory={() => setHistory({ deviceId: d.deviceId!, label: d.registerName || d.deviceId! })}
                  onReinstall={() => setReinstalling(d)}
                  onRemove={() => setRemoving(d)}
                  onFlipChannel={() => flip(d)}
                  onChanged={onChanged}
                  onReprovision={() => openReprovision(d)}
                />
              </React.Fragment>
            ))}
          </div>
        )
      ) : (
        /*
          LIST VIEW — the old 11-column table, trimmed to 7. No `min-w`: the
          identity columns it used to need 650px for (tracking, serial,
          provisioning code, order, channel, install date/source) moved onto
          the card in Task 8 — still searchable from the one box above, no
          longer paid for on every row. Secondary columns hide (not scroll)
          under `md`/`lg` so nothing forces horizontal scroll at any width.
        */
        <div className="overflow-hidden rounded-xl border border-gray-100">
          <table className="w-full table-auto text-[12.5px]">
            <thead>
              <tr className="bg-gray-50 text-left text-[10px] uppercase text-gray-400">
                <th className="font-bold py-2 px-3">Device</th>
                <th className="font-bold py-2 px-3">Status</th>
                <th className="font-bold py-2 px-3">App version</th>
                <th className="hidden font-bold py-2 px-3 md:table-cell">Errors 24h</th>
                <th className="hidden font-bold py-2 px-3 md:table-cell">Last crash</th>
                <th className="hidden font-bold py-2 px-3 md:table-cell">Target</th>
                <th className="hidden font-bold py-2 px-3 lg:table-cell">Boot / load / install</th>
                <th className="hidden font-bold py-2 px-3 md:table-cell">Last check-in</th>
                <th className="text-right font-bold py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {shown.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-gray-400">{emptyState()}</td></tr>
              )}
              {shown.map((d) => {
                const modelName = d.modelId ? modelById.get(d.modelId)?.modelName : undefined;
                // VISIBLE text only ever names the device — never an id. A row with
                // neither a register name nor a model says so in italics; deviceId/
                // serialNumber/unitId stay out of the heading (they live in `title`).
                const displayLabel = d.registerName || modelName;
                // Action labels (aria-label, not visible text) may fall back further
                // so "Support …" always names something a screen reader can say.
                const actionLabel = displayLabel || d.deviceId || d.serialNumber || d.unitId || 'this device';
                const biz = d.tenantId ? tenantNames[d.tenantId] : undefined;
                const unresolved = isUnresolved(d);
                const blocked = reinstallBlock(d);
                const mismatched = isHardwareMismatch(
                  classifyHardwareMatch(d.hardwareModel, d.modelId ? modelById.get(d.modelId)?.appProfile : undefined),
                );
                return (
                  <React.Fragment key={d.key}>
                    {d.key === noBusinessLabelKey && (
                      <tr>
                        <td colSpan={9} className="bg-gray-50 px-3 py-1.5 text-[10.5px] font-extrabold uppercase tracking-wide text-gray-400">
                          No business resolved
                        </td>
                      </tr>
                    )}
                    <tr className="border-t border-gray-100 align-top text-gray-600">
                      <td className="py-2 px-3">
                        <div className="flex min-w-0 items-center gap-2">
                          <ShimmerImage
                            src={d.modelId ? modelById.get(d.modelId)?.media?.heroImageUrl : undefined}
                            alt={modelName || displayLabel || 'Device'}
                            width={30}
                            height={30}
                            objectFit="contain"
                            className="shrink-0 rounded-lg border border-gray-200"
                            fallbackBgColor="bg-gray-100"
                            fallbackIcon={<Monitor size={14} className="text-gray-400" />}
                          />
                          <div className="min-w-0">
                            {/*
                              THE NAME IS THE WAY IN TO THE DEVICE PAGE.

                              A row with no `deviceId` is a unit that has been
                              installed but never checked in — there is no device
                              page to open for it, so it stays plain text rather
                              than becoming a link that 404s. The support drawer
                              is unchanged and still the quick path; this is for
                              everything the drawer cannot hold.
                            */}
                            {d.deviceId ? (
                              <Link
                                to={deviceHref(d.deviceId, undefined, deviceType)}
                                className={`block truncate font-bold hover:text-[#DC143C] hover:underline ${displayLabel ? 'text-gray-900' : 'italic text-gray-500'}`}
                                title={`Open this device · deviceId ${d.deviceId}${d.unitId ? ` · unitId ${d.unitId}` : ''}`}
                              >
                                {displayLabel || 'Register name unavailable'}
                              </Link>
                            ) : (
                              <b
                                className={`block truncate ${displayLabel ? 'text-gray-900' : 'italic text-gray-500'}`}
                                title={`deviceId ${d.deviceId || '—'}${d.unitId ? ` · unitId ${d.unitId}` : ''}`}
                              >
                                {displayLabel || 'Register name unavailable'}
                              </b>
                            )}
                            <div className="truncate text-[10.5px] text-gray-400">
                              {biz || (unresolved ? 'business unavailable' : '')}
                              {(biz || unresolved) && (d.locationName || unresolved) ? ' · ' : ''}
                              {d.locationName || (unresolved ? 'location unavailable' : '')}
                            </div>
                            {/*
                              THE PROVISIONING CODE, ON THE ROW.

                              It is one of the handful of strings an operator
                              actually searches a fleet by — off a ticket, a box
                              label or a phone call — so it has to be readable
                              and copyable WITHOUT opening the card. The tracking
                              id sits beside it for the same reason.
                            */}
                            {(d.provisionCode || d.trackingId) && (
                              <div className="mt-0.5 flex items-center gap-1.5 text-[10.5px]">
                                {d.trackingId && <span className="font-mono text-gray-400">{d.trackingId}</span>}
                                {d.provisionCode && (
                                  <>
                                    <span className="font-mono font-bold text-gray-700">{d.provisionCode}</span>
                                    <button
                                      type="button"
                                      onClick={() => copyProvisionCode(d.provisionCode!)}
                                      title="Copy the provisioning code"
                                      aria-label={`Copy provisioning code for ${displayLabel || d.deviceId}`}
                                      className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-400 hover:bg-[#FFF1F3] hover:text-[#DC143C]"
                                    >
                                      <Copy size={12} />
                                    </button>
                                  </>
                                )}
                              </div>
                            )}
                            {mismatched && (
                              <div className="mt-0.5 text-[10px] font-semibold text-red-600" title={`Reported ${d.hardwareModel}`}>
                                ⚠ hardware mismatch
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        <div className="flex flex-wrap items-center gap-1">
                          <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-extrabold ${STATUS_STYLE[d.releaseStatus].cls}`}>
                            {STATUS_STYLE[d.releaseStatus].label}
                          </span>
                          {/*
                            Task 10 fix round 1, Finding 3 — the same rule as the
                            card ("must say so on its face, not only in a menu")
                            applies to this view too: without this pill a frozen
                            till is indistinguishable from a healthy one here.
                          */}
                          {!d.autoUpdate && (
                            <span
                              className="rounded-full bg-rose-500/15 px-2 py-0.5 text-[10.5px] font-extrabold text-rose-600"
                              title="This till will not install app updates until auto-update is turned back on"
                            >
                              auto-update OFF
                            </span>
                          )}
                          {/* Suppressed while `unresolved`: "no unit joined" is a
                              DEFINITE claim, and the lookup that would have found
                              the unit never answered for this row. Same rule as
                              the card. */}
                          {d.hasDeviceRecord && !d.hasUnitRecord && !unresolved && (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[10.5px] font-extrabold text-amber-600">no unit joined</span>
                          )}
                          {!d.hasDeviceRecord && d.hasUnitRecord && (
                            <span className="rounded-full bg-sky-500/15 px-2 py-0.5 text-[10.5px] font-extrabold text-sky-600">installed · never checked in</span>
                          )}
                          {unresolved && (
                            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10.5px] font-extrabold text-gray-500" title="The register lookup failed for this device — this is not the same as having no register. Whether a hardware unit is paired is unknown too.">
                              register unavailable — lookup failed
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="py-2 px-3">
                        {d.displayVersion
                          ? <span className="font-mono font-bold text-gray-900" title={d.displayVersion}>{shortVersion(d.displayVersion)}</span>
                          : <span className="text-gray-400">—</span>}
                        {/* Ring/pin shown regardless of whether a version resolved — the
                            server still assigns every device a ring even before it ever
                            checks in. */}
                        <div className="text-[10.5px] text-gray-400">
                          {d.pinnedVersion ? `pinned ${d.pinnedVersion}` : d.releaseRing}
                          {d.displayVersion && <>{' · '}{d.versionSource === 'checkin' ? 'from check-in' : 'last known from install'}</>}
                        </div>
                      </td>
                      {(() => {
                        const h = d.deviceId ? health?.[d.deviceId] : undefined;
                        return (
                          <>
                            <td className="hidden py-2 px-3 md:table-cell tabular-nums">
                              {/* Bold red past ERRORS_24H_HOT_THRESHOLD — enough in a day to be worth a
                                  glance, not a hard alert level; the raw count is always shown either way. */}
                              {h ? <span className={h.errors24h > ERRORS_24H_HOT_THRESHOLD ? 'font-bold text-red-700' : 'text-stone-700'}>{h.errors24h}</span> : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="hidden py-2 px-3 md:table-cell">
                              {h?.lastCrashAt ? <span className="text-red-700">{fmtAgo(h.lastCrashAt)}</span> : <span className="text-gray-400">—</span>}
                            </td>
                          </>
                        );
                      })()}
                      <td className="hidden py-2 px-3 md:table-cell" data-testid="target-cell">
                        {d.targetVersion ? (
                          <>
                            <div className="flex items-center gap-1">
                              <span className={d.drift ? 'font-bold text-amber-600' : 'text-gray-900'}>{d.targetVersion}</span>
                              <span className={`rounded-full px-1.5 py-0.5 text-[9.5px] font-extrabold ${TARGET_SOURCE_STYLE[d.targetSource ?? 'unknown'].cls}`}>
                                {TARGET_SOURCE_STYLE[d.targetSource ?? 'unknown'].label}
                              </span>
                            </div>
                            <div className="text-[10.5px] text-gray-400">
                              {d.drift ? 'drift · ' : ''}{d.targetSource ? `via ${d.targetSource}` : 'target source not stated'}
                            </div>
                          </>
                        ) : <span className="text-gray-400">{targetsUnavailable ? 'unavailable' : '—'}</span>}
                      </td>
                      <td className="hidden py-2 px-3 lg:table-cell">
                        <div className="flex flex-wrap gap-1">
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
                            {d.bootVersion == null ? 'B —' : `B${d.bootVersion}`}
                          </span>
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold text-gray-500">
                            {d.loadingVersion == null ? 'A —' : `A${d.loadingVersion}`}
                          </span>
                          <span className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-bold">{installCell(d)}</span>
                        </div>
                      </td>
                      <td className="hidden py-2 px-3 text-gray-400 md:table-cell">
                        {d.updatedAt ? checkInAgo(d.updatedAt) : '—'}
                      </td>
                      <td className="py-2 px-3 text-right">
                        {d.deviceId ? (
                          <div className="flex items-center justify-end gap-0.5">
                            <button
                              onClick={() => setSupport(d)}
                              title="Support"
                              aria-label={`Support ${actionLabel}`}
                              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 hover:text-[#DC143C] hover:bg-[#FFF1F3]"
                            >
                              <LifeBuoy size={14} />
                            </button>
                            <button
                              onClick={() => setHistory({ deviceId: d.deviceId!, label: actionLabel })}
                              title="History"
                              aria-label={`History ${actionLabel}`}
                              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 hover:text-gray-900 hover:bg-gray-100"
                            >
                              <History size={14} />
                            </button>
                            <button
                              onClick={() => setReinstalling(d)}
                              disabled={!!blocked}
                              title={blocked || 'Reinstall'}
                              aria-label={`Reinstall ${actionLabel}`}
                              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-500 hover:text-amber-700 hover:bg-amber-50 disabled:cursor-not-allowed disabled:text-gray-300"
                            >
                              <RotateCcw size={14} />
                            </button>
                            <button
                              onClick={() => setRemoving(d)}
                              title="Remove from fleet"
                              aria-label={`Remove ${actionLabel} from the fleet`}
                              className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-gray-400 hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 size={14} />
                            </button>
                            <FleetRowMenu
                              row={d}
                              onChanged={onChanged}
                              onReprovision={() => openReprovision(d)}
                              buttonClassName="inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg text-[15px] font-extrabold text-gray-400 hover:bg-gray-100"
                            />
                          </div>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {reinstalling && (
        <ReinstallModal
          unitId={reinstalling.unitId!}
          label={reinstalling.registerName || reinstalling.trackingId || reinstalling.serialNumber || reinstalling.unitId!}
          fromState={reinstalling.unitState}
          onClose={() => setReinstalling(null)}
          onDone={onChanged}
        />
      )}

      {removing && (
        <RemoveDeviceModal
          row={removing}
          busy={removeBusy}
          onClose={() => setRemoving(null)}
          onConfirm={async () => {
            setRemoveBusy(true);
            try {
              await deviceConfigApi.deleteDevice(removing.deviceType || deviceType, removing.deviceId!);
              // Say what was actually discarded. A pin is somebody's decision,
              // and losing one silently is how a device quietly rejoins a ring.
              toastService.success(
                `Removed ${removing.registerName || removing.deviceId} from the fleet.`
                + (removing.pinnedVersion ? ` Its pin to ${removing.pinnedVersion} was discarded.` : ''),
              );
              setRemoving(null);
              onChanged();
            } catch (e: any) {
              toastService.error(e?.message || 'Could not remove the device.');
            } finally {
              setRemoveBusy(false);
            }
          }}
        />
      )}

      {history && (
        <DeviceHistoryModal
          deviceType={deviceType}
          deviceId={history.deviceId}
          deviceLabel={history.label}
          onClose={() => setHistory(null)}
        />
      )}

      {support && (
        <FleetDeviceDrawer
          row={support}
          onClose={closeSupport}
          initialAction={supportInitialAction ?? undefined}
        />
      )}
    </div>
  );
}
